import React, { useState, useEffect } from "react";

// Simple dynamic mock barcode generator
const Barcode = () => {
    return (
        <div className="bg-white p-1 rounded flex flex-col items-center justify-between h-9 w-16 select-none shadow-xs border border-gray-200">
            <div className="flex items-end justify-center space-x-[1px] h-5 w-full">
                {Array.from({ length: 15 }).map((_, i) => (
                    <div
                        key={i}
                        className="bg-black"
                        style={{
                            width: i % 3 === 0 ? '2px' : '1px',
                            height: i % 4 === 0 ? '100%' : '80%',
                        }}
                    />
                ))}
            </div>
            <span className="text-[5px] text-gray-800 font-mono tracking-tighter leading-none">
                978-3-16-148410-0
            </span>
        </div>
    );
};

const CoverGallery = ({
    covers,
    selectedCover,
    onSelect,
    title,
    bookTitle,
    bookAuthor,
    colors,
    bookDescription,
    isBackCover = false,
    coverMode = "image",
    coverBgColor = "#DC2626",
    coverTextColor = "#FFFFFF",
    coverPosition = "center",
    coverFont = "serif",
    bookType = "trade_paperback",
}) => {
    // Track load/retry state for each cover index
    const [states, setStates] = useState({});

    // Reset states when covers array changes
    useEffect(() => {
        setStates({});
    }, [covers]);

    const handleImageLoad = (index) => {
        setStates(prev => ({
            ...prev,
            [index]: { ...prev[index], loading: false, failed: false }
        }));
    };

    const handleImageError = (e, coverUrl, index) => {
        const currentState = states[index] || { retryCount: 0, failed: false, loading: true };
        
        if (coverUrl.includes("cloudinary.com")) {
            console.warn(`Cloudinary cover ${index + 1} failed to load, falling back to Pollinations.ai...`);
            const promptMatch = coverUrl.match(/e_gen_text_to_image:prompt_(.*?)\//)?.[1];
            const promptText = promptMatch ? promptMatch : "book cover";
            const fallbackUrl = `https://image.pollinations.ai/prompt/${promptText}?width=400&height=600&nologo=true`;
            
            e.target.src = fallbackUrl;
            
            setStates(prev => ({
                ...prev,
                [index]: { ...currentState, loading: true }
            }));
            return;
        }

        if (currentState.retryCount < 3) {
            const nextRetry = currentState.retryCount + 1;
            console.warn(`Pollinations.ai cover ${index + 1} failed. Retrying (${nextRetry}/3) in 2 seconds...`);
            
            setStates(prev => ({
                ...prev,
                [index]: { ...currentState, retryCount: nextRetry, loading: true }
            }));

            setTimeout(() => {
                const baseUrl = coverUrl.split('?')[0];
                const queryParams = new URLSearchParams(coverUrl.split('?')[1] || '');
                queryParams.set('retry', nextRetry.toString());
                queryParams.set('seed', Math.floor(Math.random() * 100000).toString());
                e.target.src = `${baseUrl}?${queryParams.toString()}`;
            }, 2000);
        } else {
            console.error(`Pollinations.ai cover ${index + 1} failed after 3 retries. Rendering fallback CSS cover.`);
            setStates(prev => ({
                ...prev,
                [index]: { ...currentState, failed: true, loading: false }
            }));
        }
    };

    const getFontFamily = (f) => {
        if (f === "sans-serif") return "system-ui, -apple-system, sans-serif";
        if (f === "monospace") return "Courier New, monospace";
        return "Georgia, Times New Roman, serif";
    };

    const textStyle = {
        fontFamily: getFontFamily(coverFont),
        color: coverTextColor,
    };

    const getPositionStyles = (pos) => {
        if (pos === "top") return { justifyContent: "flex-start", paddingTop: "24px" };
        if (pos === "bottom") return { justifyContent: "flex-end", paddingBottom: "24px" };
        return { justifyContent: "center" };
    };

    // Stable author portrait URL based on author's name
    const authorPortraitUrl = `https://image.pollinations.ai/prompt/professional%20headshot%20portrait%20of%20author%20named%20${encodeURIComponent(bookAuthor || "author")}%20wearing%20smart%20casual%20attire%20studio%20lighting%20plain%20background?width=150&height=150&nologo=true&seed=42`;

    return (
        <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">
                {title}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {covers.map((cover, index) => {
                    const isSelected = selectedCover === cover;
                    const state = states[index] || { retryCount: 0, failed: false, loading: true };
                    
                    const getBookAspect = (type) => {
                        const types = {
                            pocket_book: { w: 4.25, h: 6.87 },
                            small_paperback: { w: 5, h: 8 },
                            digest_size: { w: 5.5, h: 8.5 },
                            trade_paperback: { w: 6, h: 9 },
                            large_book: { w: 7, h: 10 },
                            childrens_book: { w: 8, h: 10 },
                            square_book: { w: 8.5, h: 8.5 },
                            textbook: { w: 8.5, h: 11 },
                            a5: { w: 5.83, h: 8.27 },
                            a4: { w: 8.27, h: 11.69 },
                        };
                        const dim = types[type] || types.trade_paperback;
                        return `${dim.w} / ${dim.h}`;
                    };

                    return (
                        <div
                            key={index}
                            onClick={() => onSelect(cover)}
                            style={{ aspectRatio: getBookAspect(bookType) }}
                            className={`cursor-pointer border-4 rounded-xl overflow-hidden transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg relative ${
                                isSelected ? "border-blue-600 shadow-md scale-[1.02]" : "border-transparent bg-gray-50"
                            }`}
                        >
                            {coverMode === "solid" ? (
                                /* SOLID COLOR LAYOUT */
                                <div
                                    style={{ backgroundColor: coverBgColor }}
                                    className="w-full h-full p-4 flex flex-col relative select-none"
                                >
                                    {!isBackCover ? (
                                        /* Solid Front Cover */
                                        <div 
                                            style={getPositionStyles(coverPosition)}
                                            className="flex flex-col h-full text-center"
                                        >
                                            <div style={textStyle}>
                                                <h3 
                                                    className="font-extrabold text-sm uppercase tracking-wide px-1 break-words max-h-24 overflow-hidden"
                                                >
                                                    {bookTitle || "Untitled"}
                                                </h3>
                                                <span 
                                                    className="text-[9px] tracking-[0.2em] uppercase font-bold opacity-90 truncate w-full mt-2 block"
                                                >
                                                    {bookAuthor || "Author"}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Solid Back Cover */
                                        <div className="flex flex-col h-full justify-between">
                                            {/* Description Blurb */}
                                            <div 
                                                style={{ ...textStyle, borderLeftColor: coverTextColor }}
                                                className="bg-black/25 rounded-lg p-2.5 mt-2 text-[9px] leading-relaxed italic max-h-36 overflow-hidden border-l-2"
                                            >
                                                {bookDescription ? `"${bookDescription}"` : "A fascinating journey through character and destiny."}
                                            </div>

                                            {/* Profile row */}
                                            <div className="flex justify-between items-center w-full bg-black/25 p-2 rounded-lg gap-2 mt-auto">
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={authorPortraitUrl}
                                                        alt={bookAuthor}
                                                        className="w-8 h-8 rounded-full object-cover border border-white/50"
                                                        onError={(e) => {
                                                            e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=60";
                                                        }}
                                                    />
                                                    <div className="text-left" style={textStyle}>
                                                        <div className="text-[8px] font-bold tracking-wide uppercase leading-none">{bookAuthor || "Author"}</div>
                                                        <div className="text-[6px] opacity-75 font-semibold uppercase tracking-wider mt-0.5">Writer</div>
                                                    </div>
                                                </div>
                                                <Barcode />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* IMAGE RENDER ZONE */
                                <div className="w-full h-full relative group">
                                    {/* Image Generation State Loading Overlay */}
                                    {state.loading && !state.failed && (
                                        <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px] flex flex-col items-center justify-center text-white z-20 p-4 text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-2"></div>
                                            <p className="text-[10px] font-semibold animate-pulse">
                                                {state.retryCount > 0 ? `Retrying ${state.retryCount}/3...` : "Generating artwork..."}
                                            </p>
                                        </div>
                                    )}

                                    {state.failed ? (
                                        /* IMAGE FAILED - FALLBACK SOLID COLOR RENDER */
                                        <div
                                            style={{ backgroundColor: coverBgColor }}
                                            className="w-full h-full p-4 flex flex-col justify-between items-center text-center relative select-none"
                                        >
                                            {!isBackCover ? (
                                                <div 
                                                    style={getPositionStyles(coverPosition)}
                                                    className="flex flex-col h-full text-center"
                                                >
                                                    <div style={textStyle}>
                                                        <h3 
                                                            className="font-extrabold text-sm uppercase tracking-wide px-1 break-words max-h-24 overflow-hidden"
                                                        >
                                                            {bookTitle || "Untitled"}
                                                        </h3>
                                                        <span 
                                                            className="text-[9px] tracking-[0.2em] uppercase font-bold opacity-90 truncate w-full mt-2 block"
                                                        >
                                                            {bookAuthor || "Author"}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col h-full justify-between">
                                                    <div 
                                                        style={{ ...textStyle, borderLeftColor: coverTextColor }}
                                                        className="bg-black/25 rounded-lg p-2.5 mt-2 text-[9px] leading-relaxed italic max-h-36 overflow-hidden border-l-2"
                                                    >
                                                        {bookDescription ? `"${bookDescription}"` : "A fascinating journey through character and destiny."}
                                                    </div>

                                                    <div className="flex justify-between items-center w-full bg-black/25 p-2 rounded-lg gap-2 mt-auto">
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                src={authorPortraitUrl}
                                                                alt={bookAuthor}
                                                                className="w-8 h-8 rounded-full object-cover border border-white/50"
                                                                onError={(e) => {
                                                                    e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=60";
                                                                }}
                                                            />
                                                            <div className="text-left" style={textStyle}>
                                                                <div className="text-[8px] font-bold tracking-wide uppercase leading-none">{bookAuthor || "Author"}</div>
                                                                <div className="text-[6px] opacity-75 font-semibold uppercase tracking-wider mt-0.5">Writer</div>
                                                            </div>
                                                        </div>
                                                        <Barcode />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* IMAGE LOAD SUCCESS WITH DYNAMIC TEXT OVERLAY */
                                        <div className="w-full h-full relative">
                                            <img
                                                src={cover}
                                                alt={`Book cover concept ${index + 1}`}
                                                className="w-full h-full object-cover"
                                                onLoad={() => handleImageLoad(index)}
                                                onError={(e) => handleImageError(e, cover, index)}
                                            />
                                            
                                            {!state.loading && (
                                                <div className="absolute inset-0 flex flex-col justify-between p-4 text-center pointer-events-none select-none">
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30 pointer-events-none z-0" />
                                                    
                                                    {!isBackCover ? (
                                                        <div 
                                                            style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                ...getPositionStyles(coverPosition)
                                                            }}
                                                            className="flex flex-col p-4 text-center z-10"
                                                        >
                                                            <div style={textStyle}>
                                                                <h3 
                                                                    style={{ textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}
                                                                    className="font-extrabold text-sm uppercase tracking-wide leading-snug break-words max-h-24 overflow-hidden"
                                                                >
                                                                    {bookTitle || "Untitled"}
                                                                </h3>
                                                                <span 
                                                                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}
                                                                    className="text-[9px] tracking-[0.2em] uppercase font-bold opacity-90 truncate w-full mt-2 block"
                                                                >
                                                                    {bookAuthor || "Author"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="z-10 flex flex-col h-full justify-between relative w-full">
                                                            <div 
                                                                style={{ ...textStyle, borderLeftColor: coverTextColor }}
                                                                className="bg-black/60 backdrop-blur-xs rounded-lg p-2.5 mt-2 text-[9px] leading-relaxed italic max-h-36 overflow-hidden border-l-2 shadow-sm"
                                                            >
                                                                {bookDescription ? `"${bookDescription}"` : "A fascinating journey through character and destiny."}
                                                            </div>

                                                            <div className="flex justify-between items-center w-full bg-black/70 backdrop-blur-xs p-2 rounded-lg gap-2 mt-auto border border-white/10">
                                                                <div className="flex items-center gap-2">
                                                                    <img
                                                                        src={authorPortraitUrl}
                                                                        alt={bookAuthor}
                                                                        className="w-8 h-8 rounded-full object-cover border border-white/40 shadow-xs"
                                                                        onError={(e) => {
                                                                            e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=60";
                                                                        }}
                                                                    />
                                                                    <div className="text-left" style={textStyle}>
                                                                        <div className="text-[8px] font-bold tracking-wide text-white uppercase leading-none">{bookAuthor || "Author"}</div>
                                                                        <div className="text-[6px] text-gray-300 font-semibold uppercase tracking-wider mt-0.5">Writer</div>
                                                                    </div>
                                                                </div>
                                                                <Barcode />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CoverGallery;